import { ChecklistTarget } from '@components/checklist';
import { ConditionalError } from '@components/conditional-error';
import DocumentDetailField from '@containers/form-i9/document-details-list/detail-field';
import { ExpiryDateField } from '@containers/form-i9/document-details-list/expiry-date-field';
import {
    renderPotentiallyEditableExpiryDateField,
    renderPotentiallyEditableField
} from '@containers/form-i9/document-details-list/field-content';
import {
    LabelledDocumentDetails,
    renderI9DocumentTitle
} from '@containers/form-i9/document-details-list/labelled-document-details';
import {
    ListADocumentGrid,
    PaddableBorderedGridItem,
    ThinSeparator
} from '@containers/form-i9/document-details-list/styles';
import { getDocumentDetailsListTranslations } from '@containers/form-i9/document-details-list/translations';
import { BorderedGrid } from '@containers/form-i9/styles';
import { OnboardingDocumentType } from '@dtos/document-type';
import { Grid } from '@material-ui/core';
import { Section2ValidationErrors } from '@models/onboarding-review/section2-validation-errors';
import { isSocialSecurityCard } from '@models/onboarding-review/ssn-details';
import { CompleteI9PageTranslation } from '@screens/review-section2/complete-i9/complete-i9-page-translation';
import { BorderedBox } from '@screens/review-section2/complete-i9/styles';
import { Title } from '@screens/review-section3/review/styles';
import {
    createSection2DocumentSubmissionStore,
    createTranslationService,
    createValidateDocumentDetailService,
    createValidationPostEditStore
} from '@services/factories';
import { StringFormatService } from '@services/StringFormatService';
import { DocumentDetailValidationErrorTemplate } from '@services/validate-document-detail-service';
import {
    Section2DocumentDetailsStore
} from '@stores/onboarding-review/section2-document-details-store';
import { ISection2DocumentsStore } from '@stores/onboarding-review/section2-documents-store';
import { VerticalLayout } from '@styles/spacing-styles';
import { cacheTranslations } from '@utils/cache-translations';
import { times } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useState } from 'react';
import { IssuingAuthorityField } from '@containers/form-i9/document-details-list/issuing-autority-field';
import { trimInvalidCharactersFromDocumentNumber } from '@utils/document-number-checks';
import { ISection2DocumentTypesValidation } from '@stores/onboarding-review/section2-document-types-validation-store';
import { ISection2DocumentDetails } from '@models/onboarding-review/section2-document-details';
import {I9DocExpiryDateOptionEnum} from '@swipejobs/i9-document-validations-react';
import { IValidationPostEditStore } from '@stores/validation/validation-post-edit-store';
import { RequiredWrapper } from '@components/required';
import { AdditionalInformation } from '@screens/review-section2/complete-i9/styles';
import {
  LoaderBox,
  LoaderContainer
} from '@components/loader/styles';
import { Spinner } from '@blueprintjs/core';

interface IProps {
    store: ISection2DocumentsStore;
    renderAdditionalInformation: ((listForm: string) => React.ReactNode) | React.ReactNode;
    renderConditionallyAdditionalInformation: ((listForm: string) => React.ReactNode) | React.ReactNode;
    qrCodeSection: React.ReactNode;
    validationErrors: Record<Section2ValidationErrors, boolean>;
}

const displayableListADocumentSections = 3;
const bottomBorderRow = { borderBottom: '2px solid rgba(0, 0, 0, 0.3)' };
const ExpandedDocumentDetailsList: React.FunctionComponent<IProps> = observer(props => {
    const {
        store,
        renderAdditionalInformation,
        renderConditionallyAdditionalInformation,
        qrCodeSection,
        validationErrors
    } = props;

    const workerStatus = store.applicantStatus;
    // as this maps to a single instance, this is permitted
    const documentSubmissionStore = createSection2DocumentSubmissionStore();
    const translations = getDocumentDetailsListTranslations();
    const completeI9Translations = cacheTranslations(
        createTranslationService(),
        CompleteI9PageTranslation,
        'completeI9'
    );
    const validationErrorTranslations = cacheTranslations(
        createTranslationService(),
        Section2ValidationErrors,
        'completeI9ValidationErrors'
    );

    const [[
        listBValidationPostEditStore,
        listCValidationPostEditStore,
        listAValidationPostEditStore
    ]] = useState(() => [
        createValidationPostEditStore(),
        createValidationPostEditStore(),
        createValidationPostEditStore()
    ]);

    const combinedLists = [
        {
            details: store.listB[0],
            listId: 'b',
            documentTypePlaceholder: translations.listBDocumentTypePlaceholder,
            documentNumberValidationPostEditStore: listBValidationPostEditStore
        },
        {
            details: store.listC[0],
            listId: 'c',
            documentTypePlaceholder: translations.listCDocumentTypePlaceholder,
            documentNumberValidationPostEditStore: listCValidationPostEditStore
        }
    ];

    const allDocumentTypes = store.allDocuments.map(({ documentType }) => documentType);

    const validateDocumentNumberService = createValidateDocumentDetailService();

    const validateDocumentNumber = (documentType: string, value: string, documentDetails: ISection2DocumentTypesValidation | undefined) => {

      return validateDocumentNumberService.validateDocumentNumber(
        documentType as OnboardingDocumentType,
        value,
        documentDetails
      );
    };

    const translateDocumentNumberValidationMessage = (messageTemplate: string): string => {
        const validationMessageTranslationMap = {
            [DocumentDetailValidationErrorTemplate.InvalidWithExample]: completeI9Translations.documentNumberInvalidWithExampleTemplate,
            [DocumentDetailValidationErrorTemplate.SSNMismatch]: completeI9Translations.documentNumberInvalidSSNMismatch,
            [DocumentDetailValidationErrorTemplate.SSNInvalidPattern]: completeI9Translations.documentNumberInvalidSSNPattern
        };

        return validationMessageTranslationMap[messageTemplate] ?? '';
    };

    const isIssuingAuthorityEmpty = (
      details: ISection2DocumentDetails,
      documentDetail: ISection2DocumentTypesValidation | undefined
    ): boolean => {
      return documentDetail?.freeTextAllowed
        ? !!details.issuingAuthority
        : !!details.issuingAuthorityCode;
    };

    if( store.allDocuments.length === 0 ) {
      return (
        <LoaderContainer>
          <LoaderBox className='spinner'>
            <Spinner size={30} />
          </LoaderBox>
        </LoaderContainer>
      );
    }

    return (
      <VerticalLayout innerSpacing='medium'>
        {/* List A OR List B AND List C label */}
        {workerStatus !== 'RECEIPT_REPLACEMENT' && (
          <React.Fragment>
            <VerticalLayout innerSpacing='small'>
              <Grid container={true}>
                <Grid item={true} md={4}>
                  <Title align='center'>
                    <p>{translations.listA}</p>
                    <p>{translations.listAHeading}</p>
                  </Title>
                </Grid>
                <Grid item={true} md={1}>
                  <Title align='center'>
                    <p>{translations.or.toUpperCase()}</p>
                  </Title>
                </Grid>
                <Grid item={true} md={true}>
                  <Title align='center'>
                    <p>{translations.listB}</p>
                    <p>{translations.listBHeading}</p>
                  </Title>
                </Grid>
                <Grid item={true} md={1}>
                  <Title align='center'>
                    <p>{translations.and.toUpperCase()}</p>
                  </Title>
                </Grid>
                <Grid item={true} md={true}>
                  <Title align='center'>
                    <p>{translations.listC}</p>
                    <p>{translations.listCHeading}</p>
                  </Title>
                </Grid>
              </Grid>
              <Grid container={true}>
                {/* List A */}
                <ListADocumentGrid item={true} md={5} direction='column'>
                  {times(displayableListADocumentSections, (index) => (
                    <LabelledDocumentDetails
                      key={`list-a-${index}`}
                      detailsKey={`expanded-list-a-${index}`}
                      details={store.listA[index]}
                      validationErrors={validationErrors}
                      allDocumentTypes={allDocumentTypes}
                      documentTitlePlaceholder={
                        translations.listADocumentTypePlaceholder
                      }
                      documentTypesValidation={
                        store.documentTypesValidationsAll
                      }
                    />
                  ))}
                </ListADocumentGrid>
                {/* List B AND C */}
                <BorderedGrid
                  item={true}
                  container={true}
                  md={7}
                  direction='column'
                >
                  <PaddableBorderedGridItem
                    container={true}
                    direction='column'
                    padding='none'
                  >
                    <Grid
                      item={true}
                      container={true}
                      style={bottomBorderRow}
                      direction='row'
                    >
                      {combinedLists.map(
                        ({ details, listId, documentTypePlaceholder }) => (
                          <Grid
                            item={true}
                            key={listId}
                            style={{ padding: 10 }}
                            xs={6}
                          >
                            <DocumentDetailField
                              details={details}
                              fieldName={'documentType'}
                              transformValue={renderI9DocumentTitle}
                              documentTypesValidation={
                                store.documentTypesValidationsAll
                              }
                              placeholder={documentTypePlaceholder}
                            />
                          </Grid>
                        )
                      )}
                    </Grid>
                    <Grid
                      item={true}
                      container={true}
                      style={bottomBorderRow}
                      direction='row'
                    >
                      {combinedLists.map(({ details, listId }) => {
                        const documentDetails:
                          | ISection2DocumentTypesValidation
                          | undefined = store.documentTypesValidationsAll.find(
                          (item: ISection2DocumentTypesValidation) =>
                            item.documentType === details?.documentType
                        );
                        const isValidIssuingAuthority =
                          details !== undefined &&
                          (validationErrors.documentDetailMissing
                            ? validationErrors.documentDetailMissing &&
                              isIssuingAuthorityEmpty(details, documentDetails)
                            : validationErrors.documentDetailMissing ||
                              isIssuingAuthorityEmpty(
                                details,
                                documentDetails
                              ));

                        return (
                          <Grid
                            item={true}
                            key={listId}
                            style={{ padding: 10 }}
                            xs={6}
                          >
                            <VerticalLayout innerSpacing='small'>
                              <ChecklistTarget
                                name={`expanded-list-${listId}-issuing-authority`}
                                valid={isValidIssuingAuthority}
                                canRegister={details !== undefined}
                              >
                                <IssuingAuthorityField
                                  details={details}
                                  issuingAuthorities={
                                    documentDetails?.issuingAuthority ?? []
                                  }
                                  onChange={(value) => {
                                    if (
                                      documentDetails &&
                                      !documentDetails?.freeTextAllowed &&
                                      documentDetails?.issuingAuthority
                                        ?.length > 0
                                    ) {
                                      (
                                        details as Section2DocumentDetailsStore
                                      ).setIssuingAuthorityCode(value);

                                      const documentDetail =
                                        documentDetails?.issuingAuthority.find(
                                          (item) => item.code === value
                                        );
                                      (
                                        details as Section2DocumentDetailsStore
                                      ).setIssuingAuthority(
                                        documentDetail?.displayName ?? ''
                                      );
                                    } else {
                                      (
                                        details as Section2DocumentDetailsStore
                                      ).setIssuingAuthority(value);
                                    }
                                  }}
                                  freeTextAllowed={
                                    documentDetails?.freeTextAllowed
                                  }
                                />
                              </ChecklistTarget>
                              {details && (
                                <ConditionalError
                                  condition={!isValidIssuingAuthority}
                                >
                                  {
                                    validationErrorTranslations.documentDetailMissing
                                  }
                                </ConditionalError>
                              )}
                            </VerticalLayout>
                          </Grid>
                        );
                      })}
                    </Grid>
                    <Grid
                      item={true}
                      container={true}
                      style={bottomBorderRow}
                      direction='row'
                    >
                      {combinedLists.map(
                        ({
                          details,
                          listId,
                          documentNumberValidationPostEditStore,
                        }) => {
                          const documentDetails:
                            | ISection2DocumentTypesValidation
                            | undefined =
                            store.documentTypesValidationsAll.find(
                              (item: ISection2DocumentTypesValidation) =>
                                item.documentType === details?.documentType
                            );

                          const documentNumberResult = validateDocumentNumber(
                            details?.documentType ?? '',
                            details?.number ?? '',
                            documentDetails
                          );

                                            const lastSubmittedDocumentNumber = documentSubmissionStore
                                                .lastSubmittedDocumentNumbers.get(details?.documentId ?? '') ?? '';
                                            const isSSN = isSocialSecurityCard(details?.documentType ?? '');
                                            const shouldShowSSNValidationMessage = isSSN;
                                            const showDocumentNumberSection1MismatchError = (
                                                validationErrors.documentNumberDoesNotMatchSection1 &&
                                                lastSubmittedDocumentNumber === details.number &&
                                                shouldShowSSNValidationMessage
                                            );
                                            const isValidDocumentNumber = details !== undefined && (validationErrors.documentDetailMissing ? (
                                              validationErrors.documentDetailMissing && !details.number
                                    
                                            ) : (
                                              validationErrors.documentDetailMissing || !details.number ));
                                            return (
                                                <Grid
                                                    item={true}
                                                    key={listId}
                                                    style={{ padding: 10 }}
                                                    xs={6}
                                                >
                                                    <VerticalLayout innerSpacing='small'>
                                                        <ChecklistTarget
                                                            name={`expanded-list-${listId}-number`}
                                                            valid={
                                                                !!details?.number &&
                                                                documentNumberResult.valid &&
                                                                !showDocumentNumberSection1MismatchError
                                                            }
                                                            criteria={{
                                                                fieldType: 'document-number',
                                                                isSSN
                                                            }}
                                                            canRegister={details !== undefined}
                                                        >
                                                            <DocumentDetailField
                                                                details={details}
                                                                fieldName={'number'}
                                                                placeholder={
                                                                    translations.documentNumberPlaceholder
                                                                }
                                                                renderFieldContent={renderPotentiallyEditableField(
                                                                    details !== undefined,
                                                                    (value) => {
                                                                        (details as Section2DocumentDetailsStore).setDocumentNumber(
                                                                            trimInvalidCharactersFromDocumentNumber(value)
                                                                        );

                                        if (isSSN) {
                                          documentNumberValidationPostEditStore.handleEdit();
                                        }
                                      },
                                      () => {
                                        documentNumberValidationPostEditStore.handleBlur();
                                      },
                                      translations.example,
                                      documentDetails?.example
                                    )}
                                  />
                                </ChecklistTarget>
                                {details && (
                                  <ConditionalError
                                    condition={isValidDocumentNumber}
                                  >
                                    {
                                      validationErrorTranslations.documentDetailMissing
                                    }
                                  </ConditionalError>
                                )}
                                {details !== undefined ? (
                                  <ConditionalError
                                    condition={
                                      !!details.number &&
                                      !documentNumberResult.valid &&
                                      (isSSN
                                        ? documentNumberValidationPostEditStore.showValidationMessage
                                        : true)
                                    }
                                  >
                                    {!documentNumberResult.valid &&
                                      documentNumberResult.messageTemplate ===
                                        DocumentDetailValidationErrorTemplate.InvalidWithExample &&
                                      documentNumberResult.templateArguments[0]}
                                    {!documentNumberResult.valid &&
                                      documentNumberResult.messageTemplate !==
                                        DocumentDetailValidationErrorTemplate.InvalidWithExample &&
                                      StringFormatService.format(
                                        translateDocumentNumberValidationMessage(
                                          documentNumberResult.messageTemplate
                                        ),
                                        documentNumberResult.templateArguments
                                      )}
                                  </ConditionalError>
                                ) : null}
                                {details !== undefined ? (
                                  <ConditionalError
                                    condition={
                                      !!details.number &&
                                      showDocumentNumberSection1MismatchError
                                    }
                                  >
                                    {
                                      validationErrorTranslations.documentNumberDoesNotMatchSection1
                                    }
                                  </ConditionalError>
                                ) : null}
                              </VerticalLayout>
                            </Grid>
                          );
                        }
                      )}
                    </Grid>
                    <Grid item={true} container={true} direction='row'>
                      {combinedLists.map(({ details, listId }) => {
                        let expiryDateRequired: boolean | undefined;
                        let showCheckbox: boolean | undefined;

                        let validExpiryDateField: boolean = true;
                        const documentDetails:
                          | ISection2DocumentTypesValidation
                          | undefined = store.documentTypesValidationsAll.find(
                          (item: ISection2DocumentTypesValidation) =>
                            item.documentType === details?.documentType
                        );

                        switch (documentDetails?.expiryDateOption) {
                          case I9DocExpiryDateOptionEnum.NO_EXPIRY_DATE:
                            expiryDateRequired = false;
                            showCheckbox = false;
                            validExpiryDateField = true;
                            break;
                          case I9DocExpiryDateOptionEnum.REQUIRED:
                            expiryDateRequired = true;
                            showCheckbox = false;
                            validExpiryDateField =
                              details?.expiryDate !== null &&
                              details?.expiryDate !== '' &&
                              details?.isExpiryNaCheckboxChecked !== true;
                            break;
                          case I9DocExpiryDateOptionEnum.NOT_APPLICABLE:
                            expiryDateRequired = false;
                            showCheckbox = true;
                            validExpiryDateField =
                              (details?.expiryDate === null ||
                                details?.expiryDate === '') &&
                              details?.isExpiryNaCheckboxChecked === true;
                            break;
                          default:
                            expiryDateRequired = true;
                            showCheckbox = true;
                            validExpiryDateField =
                              (details?.expiryDate !== null &&
                                details?.expiryDate !== '') ||
                              details?.isExpiryNaCheckboxChecked === true;
                        }

                        // earliest valid expiry date is tomorrow
                        // except for permanent resident cards with a replacement receipt
                        const minimumExpiryDate =
                          validateDocumentNumberService.decideMinimumDocumentExpiry(
                            documentDetails
                          );
                        return (
                          <Grid
                            item={true}
                            key={listId}
                            style={{ padding: 10 }}
                            xs={6}
                          >
                            <ChecklistTarget
                              name={`expanded-list-${listId}-expiry-date`}
                              valid={validExpiryDateField}
                              canRegister={details !== undefined}
                            >
                              <VerticalLayout innerSpacing='small'>
                                <ExpiryDateField
                                  showCheckbox={showCheckbox}
                                  details={details}
                                  onNoExpiryDateToggled={(hasNoExpiryDate) => {
                                    const documentStore =
                                      details as Section2DocumentDetailsStore;
                                    documentStore.setIsExpiryNaCheckboxChecked(
                                      hasNoExpiryDate
                                    );
                                    documentStore.setExpiryDate(
                                      hasNoExpiryDate
                                        ? null
                                        : minimumExpiryDate ?? ''
                                    );
                                  }}
                                  hideNoExpiryDate={expiryDateRequired}
                                  renderField={renderPotentiallyEditableExpiryDateField(
                                    details !== undefined,
                                    (value) => {
                                      (
                                        details as Section2DocumentDetailsStore
                                      ).setExpiryDate(value);
                                      (
                                        details as Section2DocumentDetailsStore
                                      ).setIsExpiryNaCheckboxChecked(false);
                                    },
                                    minimumExpiryDate
                                  )}
                                />
                                {details ? (
                                  <ConditionalError
                                    condition={!validExpiryDateField}
                                  >
                                    {
                                      validationErrorTranslations.documentDetailMissing
                                    }
                                  </ConditionalError>
                                ) : null}
                              </VerticalLayout>
                            </ChecklistTarget>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </PaddableBorderedGridItem>
                  <PaddableBorderedGridItem xs={true} padding='medium'>
                    <BorderedBox>{qrCodeSection}</BorderedBox>
                  </PaddableBorderedGridItem>
                </BorderedGrid>
              </Grid>
            </VerticalLayout>
            <ThinSeparator />
            <BorderedBox>
              {typeof renderAdditionalInformation === 'function'
                ? renderAdditionalInformation('expanded')
                : renderAdditionalInformation}
            </BorderedBox>
          </React.Fragment>
        )}
        {workerStatus === 'RECEIPT_REPLACEMENT' && (
          <BorderedBox>
            <RequiredWrapper>
                    <AdditionalInformation>
                        {/* {this.translations.additionalInformation} */}
                      {translations.additionalInformation}
                    </AdditionalInformation>
                </RequiredWrapper>
<Grid container={true} direction='row' spacing={2}>
          <React.Fragment>
            {store.allDocuments.map((value, index) => {
              if (value !== undefined) {
                const documentDetails:
                  | ISection2DocumentTypesValidation
                  | undefined = store.documentTypesValidationsAll.find(
                  (item: ISection2DocumentTypesValidation) =>
                    item.documentType === value?.documentType
                );
                
                let expiryDateRequired: boolean | undefined;
                let showCheckbox: boolean | undefined;

                let validExpiryDateField: boolean = true;
                let documentNumberValidationEditStore: IValidationPostEditStore;
                const isListB = store.listB.find(
                  (item) => item.documentType === value?.documentType
                );
                const isListC = store.listC.find(
                  (item) => item.documentType === value?.documentType
                );
                documentNumberValidationEditStore =
                  isListB !== undefined
                    ? listBValidationPostEditStore
                    : isListC !== undefined
                    ? listCValidationPostEditStore
                    : listAValidationPostEditStore;
                const isValidIssuingAuthority =
                  value !== undefined &&
                  (validationErrors.documentDetailMissing
                    ? validationErrors.documentDetailMissing &&
                      isIssuingAuthorityEmpty(value, documentDetails)
                    : validationErrors.documentDetailMissing ||
                      isIssuingAuthorityEmpty(value, documentDetails));
                const documentNumberResult = validateDocumentNumber(
                  value?.documentType ?? '',
                  value?.number ?? '',
                  documentDetails
                );

                const lastSubmittedDocumentNumber =
                  documentSubmissionStore.lastSubmittedDocumentNumbers.get(
                    value?.documentId ?? ''
                  ) ?? '';
                const isSSN = isSocialSecurityCard(value?.documentType ?? '');
                const shouldShowSSNValidationMessage = isSSN && true;
                const showDocumentNumberSection1MismatchError =
                  validationErrors.documentNumberDoesNotMatchSection1 &&
                  lastSubmittedDocumentNumber === value.number &&
                  shouldShowSSNValidationMessage;
                const isValidDocumentNumber =
                  value !== undefined &&
                  (validationErrors.documentDetailMissing
                    ? validationErrors.documentDetailMissing && !value.number
                    : validationErrors.documentDetailMissing || !value.number);
                switch (documentDetails?.expiryDateOption) {
                  case I9DocExpiryDateOptionEnum.NO_EXPIRY_DATE:
                    expiryDateRequired = false;
                    showCheckbox = false;
                    validExpiryDateField = true;
                    break;
                  case I9DocExpiryDateOptionEnum.REQUIRED:
                    expiryDateRequired = true;
                    showCheckbox = false;
                    validExpiryDateField =
                      value?.expiryDate !== null &&
                      value?.expiryDate !== '' &&
                      value?.isExpiryNaCheckboxChecked !== true;
                    break;
                  case I9DocExpiryDateOptionEnum.NOT_APPLICABLE:
                    expiryDateRequired = false;
                    showCheckbox = true;
                    validExpiryDateField =
                      (value?.expiryDate === null ||
                        value?.expiryDate === '') &&
                      value?.isExpiryNaCheckboxChecked === true;
                    break;
                  default:
                    expiryDateRequired = true;
                    showCheckbox = true;
                    validExpiryDateField =
                      (value?.expiryDate !== null &&
                        value?.expiryDate !== '') ||
                      value?.isExpiryNaCheckboxChecked === true;
                }

                const minimumExpiryDate =
                  validateDocumentNumberService.decideMinimumDocumentExpiry(
                    documentDetails
                  );
                return (
                  <React.Fragment>
                      <Grid item={true} direction={'column'} spacing={2} md={5}>
                        <Grid item={true}>
                        <DocumentDetailField
                      details={value}
                      fieldName={'documentType'}
                      label={index === 0 ? translations.documentTitleLabel : ''}
                      transformValue={renderI9DocumentTitle}
                      documentTypesValidation={
                        store.documentTypesValidationsAll
                      }
                      placeholder={translations.documentTitleLabel}
                    />
                        </Grid>
                        <Grid item={true} style={{marginTop:10}}>
                      <VerticalLayout innerSpacing='small'>
                      <ChecklistTarget
                        name={`expanded-list-${value.documentType}-issuing-authority`}
                        valid={isValidIssuingAuthority}
                        canRegister={value !== undefined}
                      >
                        <IssuingAuthorityField
                         showLabel={index === 0 ? true : false}
                          details={value}
                          issuingAuthorities={
                            documentDetails?.issuingAuthority ?? []
                          }
                          onChange={(issuingAuthorityValue) => {
                            if (
                              documentDetails &&
                              !documentDetails?.freeTextAllowed &&
                              documentDetails?.issuingAuthority?.length > 0
                            ) {
                              (
                                value as Section2DocumentDetailsStore
                              ).setIssuingAuthorityCode(issuingAuthorityValue);

                              const documentDetail =
                                documentDetails?.issuingAuthority.find(
                                  (item) => item.code === issuingAuthorityValue
                                );
                              (
                                value as Section2DocumentDetailsStore
                              ).setIssuingAuthority(
                                documentDetail?.displayName ?? ''
                              );
                            } else {
                              (
                                value as Section2DocumentDetailsStore
                              ).setIssuingAuthority(issuingAuthorityValue);
                            }
                          }}
                          freeTextAllowed={documentDetails?.freeTextAllowed}
                        />
                      </ChecklistTarget>
                      {value && (
                        <ConditionalError condition={!isValidIssuingAuthority}>
                          {validationErrorTranslations.documentDetailMissing}
                        </ConditionalError>
                      )}
                    </VerticalLayout>
                      </Grid>
                      <Grid item={true} style={{marginTop:10}}>
                    <VerticalLayout innerSpacing='small'>
                      <ChecklistTarget
                        name={`expanded-list-${value.documentType}-number`}
                        valid={
                          !!value?.number &&
                          documentNumberResult.valid &&
                          !showDocumentNumberSection1MismatchError
                        }
                        criteria={{
                          fieldType: 'document-number',
                          isSSN,
                        }}
                        canRegister={value !== undefined}
                      >
                        <DocumentDetailField
                           label={index === 0 ? translations.documentNumberLabel: ''}
                          details={value}
                          fieldName={'number'}
                          placeholder={translations.documentNumberPlaceholder}
                          renderFieldContent={renderPotentiallyEditableField(
                            value !== undefined,
                            (documentNumbervalue) => {
                              (
                                value as Section2DocumentDetailsStore
                              ).setDocumentNumber(
                                trimInvalidCharactersFromDocumentNumber(
                                  documentNumbervalue
                                )
                              );

                              if (isSSN) {
                                documentNumberValidationEditStore.handleEdit();
                              }
                            },
                            () => {
                              documentNumberValidationEditStore.handleBlur();
                            },
                            translations.example,
                            documentDetails?.example
                          )}
                        />
                      </ChecklistTarget>
                      {value && (
                        <ConditionalError condition={isValidDocumentNumber}>
                          {validationErrorTranslations.documentDetailMissing}
                        </ConditionalError>
                      )}
                      {value !== undefined ? (
                        <ConditionalError
                          condition={
                            !!value.number &&
                            !documentNumberResult.valid &&
                            (isSSN
                              ? documentNumberValidationEditStore.showValidationMessage
                              : true)
                          }
                        >
                          {!documentNumberResult.valid &&
                            documentNumberResult.messageTemplate ===
                              DocumentDetailValidationErrorTemplate.InvalidWithExample &&
                            documentNumberResult.templateArguments[0]}
                          {!documentNumberResult.valid &&
                            documentNumberResult.messageTemplate !==
                              DocumentDetailValidationErrorTemplate.InvalidWithExample &&
                            StringFormatService.format(
                              translateDocumentNumberValidationMessage(
                                documentNumberResult.messageTemplate
                              ),
                              documentNumberResult.templateArguments
                            )}
                        </ConditionalError>
                      ) : null}
                      {value !== undefined ? (
                        <ConditionalError
                          condition={
                            !!value.number &&
                            showDocumentNumberSection1MismatchError
                          }
                        >
                          {
                            validationErrorTranslations.documentNumberDoesNotMatchSection1
                          }
                        </ConditionalError>
                      ) : null}
                    </VerticalLayout>
                    </Grid>
                    <Grid item={true} style={{marginTop:10}}>
                    <ChecklistTarget
                      name={`expanded-list-${value.documentType}-expiry-date`}
                      valid={validExpiryDateField}
                      canRegister={value !== undefined}
                    >
                      <VerticalLayout innerSpacing='small'>
                        <ExpiryDateField
                         showLabel={index === 0 ? true : false}
                          showCheckbox={showCheckbox}
                          details={value}
                          onNoExpiryDateToggled={(hasNoExpiryDate) => {
                            const documentStore =
                              value as Section2DocumentDetailsStore;
                            documentStore.setIsExpiryNaCheckboxChecked(
                              hasNoExpiryDate
                            );
                            documentStore.setExpiryDate(
                              hasNoExpiryDate ? null : minimumExpiryDate ?? ''
                            );
                          }}
                          hideNoExpiryDate={expiryDateRequired}
                          renderField={renderPotentiallyEditableExpiryDateField(
                            value !== undefined,
                            (expiryDateValue) => 
                              (
                                value as Section2DocumentDetailsStore
                              ).setExpiryDate(expiryDateValue)
                              ,
                            minimumExpiryDate
                          )}
                        />
                        {value ? (
                          <ConditionalError condition={!validExpiryDateField}>
                            {validationErrorTranslations.documentDetailMissing}
                          </ConditionalError>
                        ) : null}
                      </VerticalLayout>
                    </ChecklistTarget>
                    </Grid>
                    </Grid>
                  </React.Fragment>
                );
              }
              return;
            })}
          </React.Fragment>
          </Grid>
          {typeof renderConditionallyAdditionalInformation === 'function'
          ? renderConditionallyAdditionalInformation('expanded')
          : renderConditionallyAdditionalInformation}
          </BorderedBox>
        )}
        
      </VerticalLayout>
    );
});

export default ExpandedDocumentDetailsList;



// WEBPACK FOOTER //
// ./src/containers/form-i9/document-details-list/expanded.tsx